import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getGroupIdFromUrl,
  getLiteReturnPath,
} from '../../utils/getGroupIdFromUrl';
import { getLiteJwtToken } from '../../api/liteAuthToken';
import {
  getActivityResult,
  type ActivityResult,
} from '../../api/liteActivitiesClient';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { MessageCircle } from 'lucide-react';
import { formatMoney } from '@pos/shared/money';

/** 由 productKey 解析出顯示名稱與單價（支援 productId:specId） */
function resolveProductKey(
  productList: Array<{
    id: string;
    name: string;
    price?: number;
    specs?: Array<{ id: string; name: string; price: number }>;
  }>,
  productKey: string
): { name: string; price: number } {
  if (!productList?.length) return { name: productKey, price: 0 };
  const idx = productKey.indexOf(':');
  if (idx > 0) {
    const productId = productKey.slice(0, idx);
    const specId = productKey.slice(idx + 1);
    const product = productList.find(p => p.id === productId);
    if (product && 'specs' in product && product.specs?.length) {
      const spec = product.specs.find(s => s.id === specId);
      if (spec) return { name: spec.name, price: spec.price };
    }
  }
  const product = productList.find(p => p.id === productKey);
  if (product && typeof (product as { price?: number }).price === 'number')
    return {
      name: product.name,
      price: (product as { price: number }).price,
    };
  return { name: productKey, price: 0 };
}

/**
 * 活動交付查看頁面
 * 顯示目前的報名狀況，不能編輯，顯示內容與按結束一樣，但沒有 checkbox
 */
export function ActivityDeliverPage() {
  const navigate = useNavigate();
  const { activityId, suffix } = useParams<{
    activityId: string;
    suffix?: string;
  }>();
  const groupId = getGroupIdFromUrl();
  const isSquareMode = Boolean(
    suffix && suffix.length >= 6 && suffix.length <= 8
  );

  const [result, setResult] = useState<ActivityResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [participantDrawerUserId, setParticipantDrawerUserId] = useState<
    string | null
  >(null);

  // 載入活動結果
  useEffect(() => {
    const loadResult = async () => {
      if (!activityId) {
        toast.error('缺少活動 ID');
        setIsLoading(false);
        return;
      }

      try {
        const jwtToken = getLiteJwtToken();
        if (!jwtToken) {
          toast.error('請先登入');
          setIsLoading(false);
          return;
        }

        const data = await getActivityResult(
          activityId,
          jwtToken,
          isSquareMode && suffix ? { squareSuffix: suffix } : undefined
        );
        setResult(data);
      } catch (error: unknown) {
        console.error('❌ [ActivityDeliverPage] 載入活動結果失敗:', error);
        toast.error(
          error instanceof Error ? error.message : '載入活動結果失敗'
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadResult();
  }, [activityId, isSquareMode, suffix]);

  const selectedParticipant =
    participantDrawerUserId && result
      ? (result.participants.find(p => p.userId === participantDrawerUserId) ??
        null)
      : null;

  const selectedParticipantTotalAmount =
    selectedParticipant && result
      ? selectedParticipant.items.reduce((sum, item) => {
          const { price } = resolveProductKey(
            result.activity.productList,
            item.productKey
          );
          return sum + item.quantity * price;
        }, 0)
      : 0;

  const formatTaiwanDateTime = (date: string) =>
    new Date(date).toLocaleString('zh-TW');

  /** 參與者列表：維持參與順序，已交付/棄單的排於下方 */
  const sortedParticipants = useMemo(() => {
    if (!result?.participants?.length) return [];
    const noStatus = result.participants.filter(
      p => !p.items.every(i => i.delivered) && !p.items.every(i => i.cancelled)
    );
    const hasStatus = result.participants.filter(
      p => p.items.every(i => i.delivered) || p.items.every(i => i.cancelled)
    );
    return [...noStatus, ...hasStatus];
  }, [result?.participants]);

  // 處理返回：Square 回 /lite/shop/:suffix，Group 回 /lite?groupId=...
  const handleBack = () => {
    navigate(
      getLiteReturnPath({
        suffix: isSquareMode ? suffix : undefined,
        groupId,
      })
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>載入中...</CardTitle>
            <CardDescription>正在取得最新參與狀況</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>載入失敗</CardTitle>
            <CardDescription>無法載入活動結果</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={handleBack} className="w-full" size="lg">
              返回
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* 標題區域 */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Button variant="ghost" onClick={handleBack} className="-ml-2 px-2">
              ← 返回
            </Button>
            <h2 className="text-2xl font-bold text-gray-900 mt-2">參與狀況</h2>
            <p className="text-sm text-gray-600 truncate">
              {result.activity.title}
            </p>
          </div>
        </div>

        {/* 參與者列表：維持參與順序，已交付/棄單的排於下方 */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>參與者列表</CardTitle>
            <CardDescription>
              依參與順序顯示，已交付/棄單排於下方；點擊項目以抽屜查看詳情
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sortedParticipants.map((participant, idx) => {
              const hasRemarks = !!(
                participant.remarks && participant.remarks.trim()
              );
              return (
                <button
                  key={participant.userId}
                  type="button"
                  className="w-full rounded-2xl border bg-white/70 p-4 text-left"
                  onClick={() => setParticipantDrawerUserId(participant.userId)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 shrink-0">
                      #{idx + 1}
                    </span>
                    <span className="font-semibold text-gray-900 truncate">
                      {participant.userName || '未命名'}
                    </span>
                    {hasRemarks && (
                      <MessageCircle
                        className="h-4 w-4 shrink-0 text-red-500"
                        aria-label="有備註"
                      />
                    )}
                    <span className="ml-auto shrink-0 text-gray-400">&gt;</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    參與時間：
                    {formatTaiwanDateTime(participant.firstParticipatedAt)}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* 參與者詳情 Drawer（Vaul） */}
        <Drawer
          open={!!participantDrawerUserId}
          onOpenChange={open => !open && setParticipantDrawerUserId(null)}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>
                {selectedParticipant?.userName || '參與者詳情'}
              </DrawerTitle>
              <DrawerDescription>
                {selectedParticipant
                  ? `參與時間：${formatTaiwanDateTime(selectedParticipant.firstParticipatedAt)}`
                  : ' '}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-6 space-y-3">
              {selectedParticipant?.remarks?.trim() && (
                <div className="rounded-xl border bg-amber-50/80 px-4 py-3">
                  <div className="text-sm font-medium text-amber-900 mb-1">
                    備註
                  </div>
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">
                    {selectedParticipant.remarks.trim()}
                  </p>
                </div>
              )}
              {selectedParticipant?.items.map(item => {
                const { name: itemName } = resolveProductKey(
                  result.activity.productList,
                  item.productKey
                );
                return (
                  <div
                    key={item.itemId}
                    className="flex items-center justify-between rounded-xl border bg-white px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {itemName}
                      </div>
                      <div className="text-sm text-gray-600">
                        數量：{item.quantity}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.delivered && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs font-medium">
                          已交付
                        </span>
                      )}
                      {item.cancelled && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-md text-xs font-medium">
                          已棄單
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 border-t flex items-center justify-between text-base font-bold">
                <span className="text-gray-900">總計</span>
                <span className="text-blue-700">
                  {formatMoney(selectedParticipantTotalAmount)}
                </span>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
}
